import React from "react"
import { Box, Text } from "@chakra-ui/react"
import {
  BadgeElement,
  BadgeElementType,
  BadgeTextData,
} from "sharedTypes/badge"
import { EDITOR_RESOLUTION } from "../utils/constants"
import InlineTextEditor from "./InlineTextEditor"

type Props = {
  element: BadgeElement
  scaleFactor?: number
  isEditing?: boolean
  onTextChange?: (newText: string) => void
  onTextChangeRealtime?: (newText: string) => void
  onStopEditing?: () => void
}

const BadgeText = ({
  element,
  scaleFactor = EDITOR_RESOLUTION,
  isEditing = false,
  onTextChange,
  onTextChangeRealtime,
  onStopEditing,
}: Props) => {
  if (element.type !== BadgeElementType.TEXT) return null

  const data = element.data as BadgeTextData

  const displayText = data.text || ""

  const fontSizeInInches = data.fontSize * (1 / 72)
  const fontSizeInMm = fontSizeInInches * 25.4 // Convert pt to mm
  const scaledFontSizeMm = fontSizeInMm * scaleFactor // Scale like canvas

  const handleTextSave = (newText: string) => {
    if (onTextChange) {
      onTextChange(newText)
    }
    if (onStopEditing) {
      onStopEditing()
    }
  }

  const handleCancel = () => {
    if (onStopEditing) {
      onStopEditing()
    }
  }

  return (
    <Box
      w="full"
      h="full"
      display="flex"
      alignItems={
        data.verticalAlign === "top"
          ? "flex-start"
          : data.verticalAlign === "bottom"
            ? "flex-end"
            : "center"
      }
      justifyContent={
        data.align === "center"
          ? "center"
          : data.align === "right"
            ? "flex-end"
            : data.align === "justify"
              ? "center" // Justify not supported in flex, fallback to center
              : "flex-start"
      }
      position="relative"
      overflow="visible"
      p={0}
      m={0}
    >
      {isEditing ? (
        <InlineTextEditor
          data={data}
          onSave={handleTextSave}
          onCancel={handleCancel}
          onChangeRealtime={onTextChangeRealtime}
          style={{
            fontSize: `${scaledFontSizeMm}px`,
            fontFamily: data.font,
            fontWeight: data.weight,
            color: data.color,
            textAlign: data.align === "justify" ? "justify" : data.align,
            fontStyle: data.italic ? "italic" : "normal",
            textDecoration:
              data.textDecoration === "underline"
                ? "underline"
                : data.textDecoration === "strikethrough"
                  ? "line-through"
                  : "none",
            textTransform:
              data.textTransform === "capitalize"
                ? "capitalize"
                : data.textTransform === "uppercase"
                  ? "uppercase"
                  : data.textTransform === "lowercase"
                    ? "lowercase"
                    : "none",
          }}
        />
      ) : (
        <Text
          fontSize={`${scaledFontSizeMm}px`}
          fontFamily={data.font}
          color={displayText ? data.color : "#999"}
          fontWeight={data.weight}
          fontStyle={data.italic ? "italic" : "normal"}
          textDecoration={
            data.textDecoration === "underline"
              ? "underline"
              : data.textDecoration === "strikethrough"
                ? "line-through"
                : "none"
          }
          textTransform={
            data.textTransform === "capitalize"
              ? "capitalize"
              : data.textTransform === "uppercase"
                ? "uppercase"
                : data.textTransform === "lowercase"
                  ? "lowercase"
                  : "none"
          }
          lineHeight="1"
          textAlign={data.align === "justify" ? "justify" : data.align}
          w={data.align === "justify" ? "full" : "auto"}
          whiteSpace="pre-wrap"
          p={0}
          m={0}
        >
          {displayText || "Enter text..."}
        </Text>
      )}
    </Box>
  )
}

export default BadgeText
